'use client';

import { useIsDesktop } from '@/_hooks/useBreakpoint';
import LocationPopoverDesktop from '@/widgets/location-popover/ui/popover-desktop';
import { useDisclosure } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { sessionActions } from '@/_store/session/session.slice';
import { IRedux } from '@/_store/types';
import {
  createPointFromCoordinates,
  findActivePoint,
  getUserLocation,
  shouldCreateNewPoint,
} from '@/_utils/location';
import Mobile from './mobile';
import { useActiveBrand } from '@/_hooks/useActiveBrand';
import { usePathname } from '@/i18n/navigation';

function OrderDestinationSelector({
  openBranches,
}: {
  openBranches: () => void;
}) {
  const [geoloc, setGeoloc] = useState<[number, number]>([0, 0]);

  const pathname = usePathname();
  const { open, setOpen } = useDisclosure();
  const isDesktop = useIsDesktop();

  const { points, branches } = useSelector((state: IRedux) => state.common);
  const { is_hall, permissions, order_type } = useSelector(
    (state: IRedux) => state.session,
  );

  const dispatch = useDispatch();
  const shipperId = useActiveBrand();
  const branch = branches?.[shipperId];

  const activePoint = useMemo(() => {
    if (!is_hall) return findActivePoint(points);
    return null;
  }, [is_hall, points]);

  // Wrapper for getting geolocation
  const getGeolocation = () => {
    if (
      !is_hall &&
      order_type?.type !== 'self-pickup' &&
      permissions?.geolocation !== 'rejected'
    ) {
      getUserLocation()
        .then((location) => {
          if (location?.latitude) {
            const coords: [number, number] = [
              location.latitude,
              location.longitude,
            ];
            setGeoloc(coords);
          } else {
            dispatch(sessionActions.rejectPGL());
            console.warn('Geolocation success but no data');
          }
        })
        .catch((err) => {
          console.log('Failed to get location:', err);
          dispatch(sessionActions.rejectPGL());
        });
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (is_hall === 'true') return;
    if (order_type?.type === 'self-pickup' && branch?.id && pathname) {
      console.log({ pathname });
    }

    if (
      permissions.geolocation === 'accepted' &&
      (order_type?.type === 'self-pickup' ? branch?.id : activePoint?.address)
    ) {
      setOpen(false);
      return;
    }

    timer = setTimeout(() => {
      getGeolocation();
      setOpen(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, [
    permissions.geolocation,
    activePoint?.address,
    order_type?.type,
    branch?.id,
    is_hall,
  ]);

  useEffect(() => {
    if (shouldCreateNewPoint(geoloc, points, activePoint)) {
      createPointFromCoordinates(geoloc, dispatch);
    }
  }, [geoloc, points, activePoint, dispatch]);

  if (is_hall === 'true') {
    return null;
  }

  if (isDesktop) {
    return (
      <LocationPopoverDesktop
        open={open}
        activePoint={activePoint}
        setOpen={setOpen}
        openBranches={openBranches}
      />
    );
  }

  return (
    <Mobile
      open={open}
      activePoint={activePoint}
      onClose={() => setOpen(false)}
    />
  );
}

export default OrderDestinationSelector;
